import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { addRevision } from "../addRevision";
import { PermilleChartData } from "../hooks/usePermilleChartData";
import { syncPatchImagesReducer, syncSetDataReducer } from "../sync";
import { DrunkDrink, Event, Photo } from "./alcoholSlice";

export const themes = {
  Light: "light",
  Dark: "dark",
  System: "system",
} as const;

export const syncProperties = [
  "name",
  "gender",
  "weight",
  "photo",
  "defaultContainers",
  "behavior",
];

const userSlice = createSlice({
  name: "user",
  initialState: {
    id: undefined,
    token: undefined,
    tag: undefined,
    name: undefined,
    weight: undefined,
    gender: undefined,
    photo: undefined,
    revision: 0,
    defaultContainers: [],
    theme: themes.System,
    behavior: {
      locationSharing: false,
      searchNearby: false,
    },
  } as User,
  reducers: {
    updateUserValue: (state, action) => {
      const { key, value } = action.payload;
      _.set(state, key, value);

      addRevision(state);
    },
    patchUser: (state, action) => {
      const patch = action.payload;
      _.merge(state, patch);

      addRevision(state);
    },
    updateUser: (state, action) => {
      const { name, weight, gender, photo, behavior } = action.payload;
      state.name = name;
      state.weight = weight;
      state.gender = gender;
      state.photo = photo;
      state.behavior = behavior;

      addRevision(state);
    },
    setDefaultContainer: (state, action) => {
      const { containerId, drinkId } = action.payload;
      let { defaultContainers } = _.cloneDeep(state);
      defaultContainers = defaultContainers.filter(
        (container) => container.drinkId !== drinkId
      );
      defaultContainers.push({ containerId, drinkId });
      state.defaultContainers = defaultContainers;

      addRevision(state);
    },
    setTheme: (state, action) => {
      const theme = action.payload;
      state.theme = theme;
    },
    syncPatchImages: syncPatchImagesReducer,
    syncSetData: syncSetDataReducer(syncProperties, "user-settings"),
  },
});

export type Theme = (typeof themes)[keyof typeof themes];

export const selectDefaultContainer =
  ({
    containerId,
    drinkId,
  }: {
    containerId?: number;
    drinkId?: number | string;
  }) =>
  (state: User) => {
    const { defaultContainers } = state;
    return (
      defaultContainers.find((c) => c.drinkId === drinkId)?.containerId ??
      containerId
    );
  };

export const genders = {
  Male: "male",
  Female: "famale",
  NotSpecified: "not_specified",
} as const;

export type User = {
  id?: string;
  token?: string;
  tag?: string;
  name?: string;
  weight?: number;
  gender?: (typeof genders)[keyof typeof genders];
  photo?: string;
  defaultContainers: {
    containerId: number;
    drinkId: number;
  }[];
  revision: number;
  theme: Theme;
  behavior: {
    locationSharing: boolean;
  };
};

export type FriendBase = {
  userId: string;
  name: string;
  tag: string;
  photo: string;
  isFriend: boolean;
  friendRequestSent: boolean;
  friendRequestReceived: boolean;
};

export type FriendFriended = FriendBase & {
  isFriend: true;
  maxPermille: number;
  permille: number;
  alcoholData: {
    sessionMaxPermille: number;
    DrunkDrinks: DrunkDrink[];
    route: { latitude: number; longitude: number; timestamp: number }[];
    events: Event[];
    photos: Photo[];
  };
  permilleChartData: PermilleChartData;
  publicSessionIds: string[];
};

export type FriendNotFriended = FriendBase & {
  isFriend: false;
};

export type Friend = FriendFriended | FriendNotFriended;

export const {
  updateUserValue,
  updateUser,
  setDefaultContainer,
  setTheme,
  syncPatchImages,
  syncSetData,
  patchUser,
} = userSlice.actions;

export const initialState = userSlice.getInitialState();

export default userSlice.reducer;
